/**
 * Grid Filter Converter
 *
 * Converts between MUI DataGrid filter models and backend query format.
 * Handles field type detection and value conversion based on column definitions.
 */

import { dateColumns } from './gridFilterUtilities'
import { convertEnrichedValueToCode, hasReferenceTable } from './dataEnrichmentUtilities'
import { FIELD_USER } from '../constants/fields'
import { TYPE_DATE, TYPE_NUMBER, TYPE_BOOLEAN, TYPE_DECIMAL } from '../constants/types'

/**
 * Detect if a field is a user field based on column definitions
 */
const isUserField = (fieldName, columnDefinitions) => {
  if (!fieldName || typeof fieldName !== 'string') return false
  const columnDef = columnDefinitions.find(col => col.value === fieldName)
  return columnDef?.field === FIELD_USER
}

/**
 * Detect if a field is a reference field (has reference table)
 */
const isReferenceField = (fieldName, columnDefinitions) => {
  if (!fieldName || typeof fieldName !== 'string') return false
  const columnDef = columnDefinitions.find(col => col.value === fieldName)
  return columnDef?.reference !== undefined
}

/**
 * Detect if a field is a date field based on column definitions
 */
const isDateField = (fieldName, columnDefinitions) => {
  if (!fieldName || typeof fieldName !== 'string') return false
  if (isUserField(fieldName, columnDefinitions)) return false

  const columnDef = columnDefinitions.find(col => col.value === fieldName)
  return (
    dateColumns.includes(fieldName) ||
    fieldName.includes('_updated') ||
    fieldName.includes('_created') ||
    columnDef?.type === TYPE_DATE ||
    columnDef?.type === 'TYPE_DATE' ||
    columnDef?.type === 3
  )
}

/**
 * Detect if a field is a number field
 */
const isNumberField = (fieldName, columnDefinitions) => {
  if (!fieldName || typeof fieldName !== 'string') return false
  const columnDef = columnDefinitions.find(col => col.value === fieldName)
  return (
    columnDef?.type === TYPE_NUMBER ||
    columnDef?.type === TYPE_DECIMAL ||
    columnDef?.type === 'TYPE_NUMBER' ||
    columnDef?.type === 2
  )
}

/**
 * Detect if a field is a boolean field
 */
const isBooleanField = (fieldName, columnDefinitions) => {
  if (!fieldName || typeof fieldName !== 'string') return false
  const columnDef = columnDefinitions.find(col => col.value === fieldName)
  return (
    columnDef?.type === TYPE_BOOLEAN ||
    columnDef?.type === 'TYPE_BOOLEAN' ||
    columnDef?.type === 'boolean' ||
    columnDef?.type === 4
  )
}

/**
 * Map MUI operator to backend operator
 */
const mapOperatorToBackend = (muiOperator, fieldName, columnDefinitions) => {
  const userField = isUserField(fieldName, columnDefinitions)
  const referenceField = isReferenceField(fieldName, columnDefinitions)

  // User field operators (dropdown)
  if (userField) {
    switch(muiOperator) {
      case 'is':
        return 'equals'
      case 'not':
        return 'notEquals'
      case 'isAnyOf':
        return 'in'
      default:
        return 'equals'
    }
  }

  // Reference field operators (dropdown)
  if (referenceField) {
    switch(muiOperator) {
      case 'is':
        return 'equals'
      case 'not':
        return 'notEquals'
      case 'isAnyOf':
        return 'in'
      default:
        return 'equals'
    }
  }

  // Date field operators
  if (isDateField(fieldName, columnDefinitions)) {
    switch(muiOperator) {
      case 'is':
        return 'equals'
      case 'not':
        return 'notEquals'
      case 'after':
        return 'gt'
      case 'onOrAfter':
        return 'gte'
      case 'before':
        return 'lt'
      case 'onOrBefore':
        return 'lte'
      default:
        return 'equals'
    }
  }

  // Number field operators
  if (isNumberField(fieldName, columnDefinitions)) {
    switch(muiOperator) {
      case '=':
      case 'equals':
        return 'equals'
      case '!=':
      case 'not':
        return 'notEquals'
      case '>':
      case 'gt':
        return 'gt'
      case '>=':
      case 'gte':
        return 'gte'
      case '<':
      case 'lt':
        return 'lt'
      case '<=':
      case 'lte':
        return 'lte'
      default:
        return 'equals'
    }
  }

  // Boolean field operators
  if (isBooleanField(fieldName, columnDefinitions)) {
    return 'equals'
  }

  // Default text operators
  switch(muiOperator) {
    case 'contains':
      return 'contains'
    case 'equals':
    case 'is':
      return 'equals'
    case 'startsWith':
      return 'startsWith'
    case 'endsWith':
      return 'endsWith'
    case 'isEmpty':
      return 'notExists'
    case 'isNotEmpty':
      return 'exists'
    case 'isAnyOf':
      return 'in'
    default:
      return 'contains'
  }
}

/**
 * Convert value based on field type
 */
const convertFilterValue = (fieldName, value, columnDefinitions, referenceTables = {}) => {
  if (value === undefined || value === null || value === '') {
    return value
  }

  // Boolean fields - backend expects string "true" or "false", not boolean primitive
  if (isBooleanField(fieldName, columnDefinitions)) {
    return String(value)
  }

  // Array of country codes - keep as-is with trailing spaces
  if (fieldName === 'regulationSpecifics.country' && Array.isArray(value)) {
    return value
  }

  // Reference fields with enriched values - convert label to code
  if (hasReferenceTable(fieldName) && typeof value === 'string') {
    return convertEnrichedValueToCode(fieldName, value, referenceTables)
  }

  return value
}

/**
 * Convert MUI DataGrid filter model to backend query format
 */
export const convertFilterModelToBackendQuery = (filterModel, columnDefinitions, referenceTables = {}) => {
  if (!filterModel || !filterModel.items || filterModel.items.length === 0) {
    return []
  }

  const query = []

  filterModel.items.forEach(item => {
    // MUI DataGrid uses 'columnField' in some versions, 'field' in others
    let fieldName = item.field || item.columnField
    const muiOperator = item.operator || item.operatorValue || 'contains'
    let queryValue = item.value

    // Skip if field name is missing
    if (!fieldName) {
      return
    }

    if (queryValue === undefined || queryValue === null || queryValue === '') {
      return // Skip empty values
    }

    // Map fullName to lastName since fullName is a computed field that doesn't exist in DB
    if (fieldName === 'fullName') {
      fieldName = 'lastName'
    }

    // Convert the operator based on field type
    const backendOperator = mapOperatorToBackend(muiOperator, fieldName, columnDefinitions)

    // Convert the value based on field type
    queryValue = convertFilterValue(fieldName, queryValue, columnDefinitions, referenceTables)

    // Determine field type for backend
    let fieldType = 'text'
    if (isDateField(fieldName, columnDefinitions)) {
      fieldType = 'date'
    } else if (isNumberField(fieldName, columnDefinitions)) {
      fieldType = 'number'
    } else if (isBooleanField(fieldName, columnDefinitions)) {
      fieldType = 'boolean'
    }

    query.push({
      id: item.id || Math.random().toString(36).substr(2, 9),
      children: [],
      type: fieldType,
      field: fieldName,
      operator: backendOperator,
      value: queryValue
    })
  })

  return query
}

/**
 * Convert backend query to MUI DataGrid filter model
 */
export const convertBackendQueryToFilterModel = (query, columnDefinitions) => {
  if (!query || query.length === 0) {
    return { items: [] }
  }

  const items = query.map(condition => {
    const fieldName = condition.field
    const backendOperator = condition.operator

    // Map backend operator back to MUI operator
    let muiOperator
    if (isDateField(fieldName, columnDefinitions)) {
      switch(backendOperator) {
        case 'equals':
          muiOperator = 'is'
          break
        case 'notEquals':
          muiOperator = 'not'
          break
        case 'gt':
          muiOperator = 'after'
          break
        case 'gte':
          muiOperator = 'onOrAfter'
          break
        case 'lt':
          muiOperator = 'before'
          break
        case 'lte':
          muiOperator = 'onOrBefore'
          break
        default:
          muiOperator = 'is'
      }
    } else {
      switch(backendOperator) {
        case 'equals':
          muiOperator = 'equals'
          break
        case 'notEquals':
          muiOperator = 'not'
          break
        case 'contains':
          muiOperator = 'contains'
          break
        case 'startsWith':
          muiOperator = 'startsWith'
          break
        case 'endsWith':
          muiOperator = 'endsWith'
          break
        case 'notExists':
          muiOperator = 'isEmpty'
          break
        case 'exists':
          muiOperator = 'isNotEmpty'
          break
        case 'in':
          muiOperator = 'isAnyOf'
          break
        case 'gt':
          muiOperator = '>'
          break
        case 'gte':
          muiOperator = '>='
          break
        case 'lt':
          muiOperator = '<'
          break
        case 'lte':
          muiOperator = '<='
          break
        default:
          muiOperator = 'contains'
      }
    }

    return {
      id: condition.id,
      field: fieldName,
      columnField: fieldName,  // Support both field names for different MUI versions
      operator: muiOperator,
      operatorValue: muiOperator,
      value: condition.value
    }
  })

  return { items }
}
